import type { BatchResponse } from '@carboncopyco/types';
import type {
  Api,
  EndpointDefinitions,
} from '@reduxjs/toolkit/dist/query/react';
import type {
  QueryKeys,
  RootState,
} from '@reduxjs/toolkit/src/query/core/apiState';
import type { ThunkDispatch } from 'redux-thunk';
import {
  type DeleteRecordingArgs,
  type GetWorkspaceAssetsArgs,
  type SearchResult,
  folderApi,
  workspaceApi,
} from '../mainApi';

type QueryDataUpdateHandlerArgs = {
  state: RootState<any, any, string>;
  endpointName: QueryKeys<EndpointDefinitions>;
  endpointArgumentKey: string;
  endpointArgumentValue: string;
  dispatch: ThunkDispatch<RootState<any, any, string>, any, any>;
  updateQueryDataFunction: (
    assets: BatchResponse<SearchResult>
  ) => BatchResponse<SearchResult>;
  api?: Api<any, any, any, any, any>;
};

type WorkspaceAddAssetArgs = {
  queryFulfilled: Promise<any>;
  state: RootState<any, any, string>;
  dispatch: ThunkDispatch<RootState<any, any, string>, any, any>;
  argumentKey: string;
  entityType: 'record' | 'demo' | 'tour' | 'overlay' | 'playbook';
};

const getQueryArgs = (
  queries,
  endpointName,
  endpointArgumentKey,
  endpointArgumentValue
) => {
  const workspaceArgs = [];
  Object.keys(queries).forEach((key) => {
    const query = queries[key];
    const queryArgs = query?.originalArgs as GetWorkspaceAssetsArgs;
    if (
      query?.endpointName === endpointName &&
      queryArgs[endpointArgumentKey]?.toString() ===
        endpointArgumentValue?.toString()
    ) {
      workspaceArgs.push(query.originalArgs);
    }
  });
  return workspaceArgs;
};
export const queryDataUpdateHandler = ({
  state,
  endpointName,
  endpointArgumentKey,
  endpointArgumentValue,
  dispatch,
  updateQueryDataFunction,
  api = workspaceApi,
}: QueryDataUpdateHandlerArgs) => {
  const queries = state.mainApi.queries;
  const workspaceArgs = getQueryArgs(
    queries,
    endpointName,
    endpointArgumentKey,
    endpointArgumentValue
  );
  workspaceArgs.forEach((workspaceArg) => {
    dispatch(
      api.util.updateQueryData(
        endpointName,
        (workspaceArg as never) || undefined,
        (assets: BatchResponse<SearchResult>) => updateQueryDataFunction(assets)
      )
    );
  });
};

export const workspaceRemoveAsset = (
  workspaceAssets: BatchResponse<SearchResult>,
  arg: DeleteRecordingArgs
) => ({
  ...workspaceAssets,
  items: workspaceAssets.items.filter(
    (asset) => asset.id?.toString() !== arg.id?.toString()
  ),
});

export const workspaceAddAsset = async ({
  queryFulfilled,
  state,
  dispatch,
  argumentKey,
  entityType,
}: WorkspaceAddAssetArgs) => {
  try {
    const { data: updatedAsset } = await queryFulfilled;
    const entityTypeToUpdate =
      updatedAsset?.recordingType && updatedAsset?.recordingType === 'live_demo'
        ? 'overlay'
        : entityType;
    const endpointName =
      String(updatedAsset.folderId) === '0'
        ? 'getWorkspaceAssets'
        : 'getFolderAssets';
    queryDataUpdateHandler({
      state,
      dispatch,
      endpointName: endpointName as QueryKeys<EndpointDefinitions>,
      endpointArgumentKey: argumentKey,
      endpointArgumentValue: updatedAsset[argumentKey],
      updateQueryDataFunction: (workspaceAssets) => {
        workspaceAssets.items.unshift({
          ...updatedAsset,
          entityType: entityTypeToUpdate,
        });
        return workspaceAssets;
      },
    });
  } catch (e) {
    console.error(e);
  }
};

export const createOnQueryStartedPerContext = () => {
  return async (arg, { dispatch, getState, queryFulfilled }) => {
    try {
      const { data } = await queryFulfilled;
      const isInsideFolder = String(arg.folderId) !== '0';
      const state = getState();
      const config = isInsideFolder
        ? {
            api: folderApi,
            endpointName: 'getFolderAssets' as QueryKeys<EndpointDefinitions>,
            endpointArgumentKey: 'folderId',
            endpointArgumentValue: arg.folderId,
          }
        : {
            api: workspaceApi,
            endpointName:
              'getWorkspaceAssets' as QueryKeys<EndpointDefinitions>,
            endpointArgumentKey: 'workspaceId',
            endpointArgumentValue: data.workspaceId,
          };

      queryDataUpdateHandler({
        ...config,
        state,
        dispatch,
        updateQueryDataFunction: (assets) => workspaceRemoveAsset(assets, arg),
      });
    } catch (e) {
      console.error(e);
    }
  };
};
